import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { useUnsavedChangesGuard } from '../unsaved-changes';
import { OfflineShell } from './OfflineShell';
import { useNetworkStatus } from './network-status';

function logPwaIssue(context: string, error: unknown) { console.warn(`[PWA] ${context}`, error); }

export function PwaLifecycle() {
  const { locale } = useI18n();
  const confirmDiscard = useUnsavedChangesGuard();
  const online = useNetworkStatus();
  const [notice, setNotice] = useState('');
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [updateApplying, setUpdateApplying] = useState(false);
  const reloadForUpdate = useRef(false);

  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
    let registration: ServiceWorkerRegistration | null = null;
    let updateFound: (() => void) | null = null;
    let lastUpdateCheck = 0;
    const showWaitingWorker = (worker: ServiceWorker) => { setNotice(''); setWaitingWorker(worker); };
    const onControllerChange = () => { if (reloadForUpdate.current) window.location.reload(); };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    const checkForUpdate = () => {
      if (!registration || !navigator.onLine || document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastUpdateCheck < 60_000) return;
      lastUpdateCheck = now;
      registration.update().catch(error => logPwaIssue('service worker update check failed', error));
    };
    const onVisibility = () => checkForUpdate();
    window.addEventListener('online', checkForUpdate);
    document.addEventListener('visibilitychange', onVisibility);
    navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).then(nextRegistration => {
      registration = nextRegistration;
      if (registration.waiting && navigator.serviceWorker.controller) showWaitingWorker(registration.waiting);
      updateFound = () => {
        const worker = registration?.installing;
        if (!worker) return;
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) showWaitingWorker(registration?.waiting ?? worker);
        });
      };
      registration.addEventListener('updatefound', updateFound);
      checkForUpdate();
    }).catch(error => logPwaIssue('service worker registration failed', error));
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      window.removeEventListener('online', checkForUpdate);
      document.removeEventListener('visibilitychange', onVisibility);
      if (registration && updateFound) registration.removeEventListener('updatefound', updateFound);
    };
  }, []);

  function applyUpdate() {
    if (!waitingWorker || updateApplying) return;
    if (!confirmDiscard()) { setNotice(locale === 'zh-CN' ? '已保留当前编辑；保存后再更新即可。' : 'Your edit was kept. Save it before applying the update.'); return; }
    reloadForUpdate.current = true;
    setUpdateApplying(true);
    setNotice(locale === 'zh-CN' ? '正在切换到新版本…' : 'Switching to the new version…');
    waitingWorker.postMessage({ type: 'ACTIVATE_UPDATE' });
    window.setTimeout(() => {
      if (!reloadForUpdate.current) return;
      setUpdateApplying(false);
      setNotice(locale === 'zh-CN' ? '更新尚未完成，请稍后重试。' : 'The update has not completed yet. Please try again.');
    }, 8_000);
  }

  return <>
    {!online && <OfflineShell />}
    {waitingWorker && online && <aside className="pwa-card pwa-update-card" role="status" aria-live="polite">
      <div><strong>{locale === 'zh-CN' ? '新版本已准备' : 'A new version is ready'}</strong><p>{notice || (locale === 'zh-CN' ? '不会自动刷新。保存当前编辑后再更新，避免丢失未保存内容。' : 'The page will not refresh automatically. Save current edits before updating.')}</p></div>
      <div className="pwa-card-actions"><button type="button" className="primary-button compact" onClick={applyUpdate} disabled={updateApplying}>{updateApplying ? (locale === 'zh-CN' ? '更新中…' : 'Updating…') : (locale === 'zh-CN' ? '安全更新' : 'Update safely')}</button></div>
    </aside>}
  </>;
}
